import { useTranslation } from 'react-i18next'

import { Box, Grid, Text } from '@enechain/ecloud-designsystem'

import type { ExecutionKpi } from '~/components/bidding/common/executionSummary'

type Props = {
  kpi: ExecutionKpi
  /**
   * 約定総量のうちブロック入札の分 (MW)。 0 より大きい時だけ約定総量のカードに添え、
   * 通常入札とブロック入札を合算した数字だと読めるようにする (design `#srk-buy-blk`)。
   */
  blockExecVolumeMw?: { buy: number; sell: number }
}

const DASH = '—'

/**
 * スポット約定結果の KPI カード 4 枚 (AC-BSP-002a)。
 * 約定総量(買) MW / 約定総量(売) MW / 加重平均約定価格 / 約定率 (%)。
 * 収支のカードは持たない (AC-BSP-090: 手数料が固定か従量かは事業者の契約によって違い、
 * 単価 1 本の式では正しい収支にならないため)。
 *
 * design: `design/bidding/spot.html` の `.spot-result-kpis` (`.ecl-kpi-card` を
 * `repeat(auto-fit, minmax(170px, 1fr))` で並べる = 枚数差をレイアウトで吸収する
 * Des 判断 #3852)。 時間前 (AC-BIN-021) は 6 枚で枚数が違うため、 カードの器は
 * 共通化せず「同じ寸法感 (auto-fit / min 170px)」 で揃える。
 *
 * 買 / 売 の識別色 (bid / offer) は流用しない (= 買 / 売 の約定総量に色を付けない)。
 */
export const SpotExecutionKpiCards = ({
  kpi,
  blockExecVolumeMw,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const blockShare = (volume: number | undefined): string | undefined =>
    volume !== undefined && volume > 0
      ? t('page:biddingSpot.blockExec.blockShare', {
          volume: volume.toFixed(1),
        })
      : undefined

  return (
    <Grid
      alignItems="stretch"
      gridGap={3}
      gridTemplateColumns="repeat(auto-fit, minmax(170px, 1fr))"
    >
      <KpiCard
        label={t('page:biddingSpot.executionKpi.buyVolume')}
        meta={blockShare(blockExecVolumeMw?.buy)}
        unit="MW"
        value={kpi.buyExecVolumeMw.toFixed(1)}
      />
      <KpiCard
        label={t('page:biddingSpot.executionKpi.sellVolume')}
        meta={blockShare(blockExecVolumeMw?.sell)}
        unit="MW"
        value={kpi.sellExecVolumeMw.toFixed(1)}
      />
      {/* 単価カードだけ unit を渡さない。 値に「¥」 を前置する design
          (`spot.html:11792` の `'¥' + (priceSum / priceMw).toFixed(2)`) に合わせており、
          unit="円/kWh" も付けると「¥11.67 円/kWh」 と単位が二重になる。 */}
      <KpiCard
        label={t('page:biddingSpot.executionKpi.avgPrice')}
        value={
          kpi.weightedAvgPriceYenPerKwh === undefined
            ? DASH
            : `¥${kpi.weightedAvgPriceYenPerKwh.toFixed(2)}`
        }
      />
      <KpiCard
        label={t('page:biddingSpot.executionKpi.fillRate')}
        unit="%"
        value={
          kpi.fillRatePct === undefined ? DASH : kpi.fillRatePct.toFixed(1)
        }
      />
    </Grid>
  )
}

type KpiCardProps = {
  label: string
  value: string
  unit?: string
  meta?: string
}

const KpiCard = ({
  label,
  value,
  unit,
  meta,
}: KpiCardProps): React.ReactElement => (
  <Box
    padding={3}
    border="1px solid"
    borderColor="Border.lowEmphasis"
    borderRadius="md"
    backgroundColor="Surface.primary"
  >
    {/* design `.ecl-kpi-card` の label = 14px/400 mid-emphasis、 value = 20px/700。 */}
    <Text color="Text.midEmphasis" fontSize="md">
      {label}
    </Text>
    {/* eslint-disable-next-line ebalance/no-adhoc-typography -- KPI 値 xl/bold (design `.kpi-value` = font-size-xl / weight 700。 IntradayBidding ExecutionResultKpiGrid と同型) */}
    <Text
      color="Text.highEmphasis"
      fontSize="xl"
      fontWeight="bold"
      css={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {value}
      {/* 単位は value の中で数値だけを強調する `.ecl-kpi-unit` = 14px / 400 /
          Text.midEmphasis / 左 2px (UI 辞書 `components/summary-kpi-cards.md`)。
          [DS差異] textStyle (body = 14px) に weight 400 を併記する: 親の value
          (xl / 700) の中に置くので、 weight を書かないと 700 を継承する
          (DS の textMd は app の system で fontSize が解決されず使えない)。 */}
      {unit !== undefined && (
        <Text
          as="span"
          textStyle="body"
          marginLeft="0.5"
          color="Text.midEmphasis"
          fontWeight="normal"
        >
          {unit}
        </Text>
      )}
    </Text>
    {meta !== undefined && (
      <Text textStyle="captionNormal" color="Text.midEmphasis">
        {meta}
      </Text>
    )}
  </Box>
)
